<template>
  <AIVDialog
    v-model="isVisible"
    title="Clock Out"
    width="40%"
    height="85vh"
    class="mobile-friendly-modal clock-out-modal"
  >
    <div class="clock-modal-content">
      <div class="mb-3">
        <h5>Shift Details</h5>
        <p><strong>Date:</strong> {{ formatTableDate(shift?.date) }}</p>
        <p>
          <strong>Time:</strong>
          {{
            formatTimeRange(
              shift?.scheduled_start || shift?.startTime,
              shift?.scheduled_end || shift?.endTime,
              shift?.date,
            )
          }}
        </p>
        <p>
          <strong>Payer:</strong>
          {{ shift?.payer || shift?.originalData?.payer || "Not specified" }}
        </p>
        <p v-if="shift?.payer !== 'Non Service'"><strong>Client:</strong> {{ formatClient(shift) }}</p>
        <p><strong>Caregiver:</strong> {{ formatCaregiver(shift) }}</p>
        <p><strong>Service Code:</strong> {{ formatServiceCode(shift) }}</p>
        <p>
          <strong>Clock In Time:</strong>
          {{
            formatClockTime(
              myClockInTime ||
                shift?.clock_in ||
                shift?.clockIn ||
                shift?.originalData?.clockIn ||
                shift?.originalData?.clock_in ||
                shift?.actualStartTime,
            )
          }}
        </p>
        <p v-if="clientLocationInfo && shift?.payer !== 'Non Service' && shift?.shift_type !== 'Group'">
          <strong>Client Address:</strong> {{ clientLocationInfo.address }}
          <el-button
            type="text"
            size="small"
            @click="openMapLinkFromAddress(clientLocationInfo.address)"
            class="map-link-btn"
          >
            <i class="fa-solid fa-location-dot"></i> View on Map
          </el-button>
        </p>
      </div>

      <!-- Virtual meeting link (for virtual shifts) -->
      <div v-if="isVirtualShift && shift && shift.virtual_link" class="location-section">
        <label class="form-label">Meeting Link:</label>
        <div class="location-info-card">
          <div class="location-row">
            <span class="location-label">
              <i class="fa-solid fa-video"></i> Meeting:
            </span>
            <a
              :href="shift.virtual_link"
              target="_blank"
              rel="noopener noreferrer"
              class="location-value virtual-link-value"
            >
              {{ shift.virtual_link }}
            </a>
          </div>
        </div>
      </div>

      <!-- Location Section (for LS and JC services) -->
      <div v-if="requiresLocation && !isVirtualShift" class="location-section">
        <label class="form-label">Location Information:</label>

        <div class="location-info-card">
          <!-- Your Location -->
          <div class="location-row">
            <span class="location-label"><i class="fa-solid fa-user"></i> Your Location:</span>
            <span v-if="currentLocation" class="location-value">
              {{ currentLocation.latitude.toFixed(6) }}, {{ currentLocation.longitude.toFixed(6) }}
            </span>
            <span v-else-if="gettingLocation" class="location-pending">
              <i class="fa-solid fa-spinner fa-spin"></i> Getting your location...
            </span>
            <span v-else class="location-pending">
              Location unavailable
            </span>
          </div>

          <!-- Client Location -->
          <div v-if="clientLocationInfo" class="location-row">
            <span class="location-label"><i class="fa-solid fa-building"></i> Client Location:</span>
            <span class="location-value">{{ clientLocationInfo.address }}</span>
          </div>

          <!-- Distance -->
          <div v-if="currentLocation && clientLocationInfo?.geocode" class="location-row">
            <span class="location-label"><i class="fa-solid fa-route"></i> Distance:</span>
            <span class="location-value">
              {{ formatDistance(calculatedDistance) }}
            </span>
          </div>

        </div>
      </div>

      <!-- Generic VR activity pickers (LS/SED/SB and any other VR code with an
           admin-managed activity list) -->
      <div
        v-for="code in isSideActor ? [] : vrCodesWithActivities"
        :key="'vract-' + code"
        class="mb-3"
      >
        <label class="form-label">
          {{ vrActivityState[code].name }} — Activity Completed
          <template v-if="maxForCode(code) != null">(Select up to {{ maxForCode(code) }})</template>
        </label>
        <AIVSelect
          :value="vrActivityState[code].selectedIds"
          @input="onVrActivitySelect(code, $event)"
          :options="vrActivityState[code].options"
          placeholder="Select activities"
          :multi-select="true"
          :searchable="true"
          :custom-style="{ width: '100%' }"
        />
      </div>

      <!-- Legacy Job Development fields (CE/SE/SJBT/TWE): shown only for
           shifts already documented on the jd_* columns, or when the code has
           no configured questions (see showJdFields) -->
      <div v-if="showJdFields && !isSideActor">
        <div class="mb-3">
          <label class="form-label">Job Development Activity Completed</label>
          <div class="shift-notes-description mb-2">
            <div>
              Please include:
              <ul>
                <li>Places applied to that shift</li>
                <li>Employer follow up completed</li>
                <li>Informational interviews completed</li>
                <li>Resume development progress</li>
                <li>
                  Information about where an interview occurred and preparation
                </li>
              </ul>
            </div>
          </div>
          <el-input
            v-model="jdActivity"
            type="textarea"
            :rows="4"
            placeholder="Describe job development activities completed..."
          />
        </div>

        <div class="mb-3">
          <label class="form-label">Outcome of Activity</label>
          <div class="shift-notes-description mb-2">
            <div>
              Please include:
              <ul>
                <li>
                  List if you helped the client apply or if they will be
                  applying on their own
                </li>
                <li>Describe how follow up went</li>
                <li>
                  Describe how the interview went and the date they will hear
                  back
                </li>
                <li>
                  Describe if you were able to finish the resume development or
                  if information is still needed
                </li>
              </ul>
            </div>
          </div>
          <el-input
            v-model="jdOutcome"
            type="textarea"
            :rows="4"
            placeholder="Describe the outcome of activities..."
          />
        </div>

        <div class="mb-3">
          <label class="form-label">Next Steps Forward</label>
          <div class="shift-notes-description mb-2">
            <div>
              Please include:
              <ul>
                <li>
                  What is the plan for your next meeting for job development and
                  are you planning on doing any job development on their behalf
                  before the next meeting?
                </li>
                <li>
                  If there is any follow up with employers or applications that
                  are supposed to be completed by the client, include that
                  information here
                </li>
              </ul>
            </div>
          </div>
          <el-input
            v-model="jdNextSteps"
            type="textarea"
            :rows="4"
            placeholder="Describe next steps forward..."
          />
        </div>
      </div>

      <!-- JC Service Code Specific Fields -->
      <div v-if="isJCService && !isSideActor">
        <div class="mb-3">
          <label class="form-label"
            >Primary Job Coaching Activities (Select up to 3)</label
          >
          <AIVSelect
            v-model="selectedJCPrimaryActivities"
            :options="jobCoachingActivities"
            placeholder="Select primary activities"
            :multi-select="true"
            :searchable="true"
            :custom-style="{ width: '100%' }"
            :loading="loadingJobCoachingActivities"
          />
        </div>

        <div class="mb-3">
          <label class="form-label"
            >Secondary Job Coaching Activities (Select up to 3)</label
          >
          <AIVSelect
            v-model="selectedJCSecondaryActivities"
            :options="jobCoachingActivities"
            placeholder="Select secondary activities"
            :multi-select="true"
            :searchable="true"
            :custom-style="{ width: '100%' }"
            :loading="loadingJobCoachingActivities"
          />
        </div>
      </div>

      <!-- Medication Administration -->
      <div v-if="clientMedicationGroups.length > 0 && isDSPDOneOnOneShift && !isSideActor" class="mb-3">
        <h6 class="form-label">Medication Administration <span style="color: #f56c6c;">*</span></h6>
        <div v-for="group in clientMedicationGroups" :key="group.clientId">
          <div v-if="clientMedicationGroups.length > 1" class="medication-client-name">
            {{ group.clientName }}
          </div>
          <div v-for="med in group.medications" :key="`${group.clientId}_${med.id}`" class="medication-admin-item mb-2">
            <div class="med-header">
              <div>
                <strong>{{ med.name }}</strong> - {{ med.dosage }}
                <span v-if="med.frequency">({{ med.frequency }})</span>
                <span v-if="med.administration_times && med.administration_times.length > 0" class="med-time-hint"> · {{ med.administration_times.map(t => formatAdminTime(t)).join(', ') }}</span>
              </div>
            </div>
            <div class="med-action-buttons mt-1">
              <el-button
                size="small"
                :type="medicationAdministrations[`${group.clientId}_${med.id}`].wasAdministered === true ? 'success' : 'default'"
                @click="$set(medicationAdministrations[`${group.clientId}_${med.id}`], 'wasAdministered', true)"
              >Given</el-button>
              <el-button
                size="small"
                :type="medicationAdministrations[`${group.clientId}_${med.id}`].wasAdministered === false ? 'danger' : 'default'"
                @click="$set(medicationAdministrations[`${group.clientId}_${med.id}`], 'wasAdministered', false)"
              >Skipped</el-button>
            </div>
            <div v-if="medicationAdministrations[`${group.clientId}_${med.id}`].wasAdministered === true" class="mt-1">
              <span style="color: #e0e0e0; font-size: 0.85rem;">Time Given:</span>
              <AIVTimeSelect
                v-model="medicationAdministrations[`${group.clientId}_${med.id}`].administeredAtTime"
                :minute-interval="5"
                :allow-exact-minutes="true"
                placeholder="Time given"
              />
            </div>
            <div v-if="medicationAdministrations[`${group.clientId}_${med.id}`].wasAdministered === false" class="mt-1">
              <AIVSelect
                :value="medicationAdministrations[`${group.clientId}_${med.id}`].skipReasonCategory"
                @input="val => { $set(medicationAdministrations[`${group.clientId}_${med.id}`], 'skipReasonCategory', val); if (val !== 'Other') $set(medicationAdministrations[`${group.clientId}_${med.id}`], 'skipReason', val); }"
                :options="medicationSkipReasons"
                placeholder="Select reason for skipping..."
              />
              <el-input
                v-if="medicationAdministrations[`${group.clientId}_${med.id}`].skipReasonCategory === 'Other'"
                v-model="medicationAdministrations[`${group.clientId}_${med.id}`].skipReason"
                placeholder="Please specify reason..."
                size="small"
                class="mt-1"
              />
            </div>
            <div v-if="medicationAdministrations[`${group.clientId}_${med.id}`].wasAdministered !== null" class="med-initial mt-1">
              <i class="fa-solid fa-signature"></i>
              <span>Initialed by: <strong>{{ formatCaregiver(shift) }}</strong></span>
            </div>
          </div>
        </div>
      </div>

      <!-- DSPD 1-on-1 shift: service code questions, goals, and concerns -->
      <div v-if="isDSPDOneOnOneShift && !isSideActor">
        <div
          v-for="q in visibleDspdQuestions"
          :key="'dspd-q-' + q.id"
          class="mb-3"
        >
          <label class="form-label">
            {{ q.question_text }}
            <span v-if="q.required" style="color: #f56c6c;">*</span>
          </label>
          <div
            v-if="q.description"
            class="shift-notes-description mb-2"
            style="white-space: pre-line;"
          >{{ q.description }}</div>
          <el-input
            v-if="q.question_type === 'free_response' || !q.question_type"
            :value="dspdQuestionAnswers[q.id] || ''"
            @input="$set(dspdQuestionAnswers, q.id, $event)"
            type="textarea"
            :rows="2"
            placeholder="Enter your answer..."
          />
          <div v-else-if="q.question_type === 'yes_no'" class="yes-no-buttons">
            <el-button
              :type="dspdQuestionAnswers[q.id] === 'yes' ? 'primary' : 'default'"
              size="medium"
              @click="$set(dspdQuestionAnswers, q.id, 'yes')"
            >Yes</el-button>
            <el-button
              :type="dspdQuestionAnswers[q.id] === 'no' ? 'danger' : 'default'"
              size="medium"
              @click="$set(dspdQuestionAnswers, q.id, 'no')"
            >No</el-button>
          </div>
          <el-select
            v-else-if="q.question_type === 'select'"
            :value="dspdQuestionAnswers[q.id] || ''"
            @input="$set(dspdQuestionAnswers, q.id, $event)"
            placeholder="Select an option"
            style="width: 100%;"
          >
            <el-option
              v-for="(opt, i) in (q.options || [])"
              :key="i"
              :label="opt"
              :value="opt"
            />
          </el-select>
          <el-select
            v-else-if="q.question_type === 'multi_select'"
            multiple
            :value="parseMultiAnswer(dspdQuestionAnswers[q.id])"
            @input="$set(dspdQuestionAnswers, q.id, joinMultiAnswer($event))"
            placeholder="Select all that apply"
            style="width: 100%;"
          >
            <el-option
              v-for="(opt, i) in (q.options || [])"
              :key="i"
              :label="opt"
              :value="opt"
            />
          </el-select>
        </div>

        <div
          v-for="goal in dspdClientGoals"
          :key="'dspd-g-' + goal.id"
          class="mb-3"
        >
          <label class="form-label">
            {{ goal.question_text }}
            <span style="color: #f56c6c;">*</span>
          </label>
          <el-input
            v-if="goal.question_type === 'free_response' || !goal.question_type"
            :value="dspdGoalAnswers[goal.id] || ''"
            @input="$set(dspdGoalAnswers, goal.id, $event)"
            type="textarea"
            :rows="2"
            placeholder="Enter your answer..."
          />
          <div v-else-if="goal.question_type === 'yes_no'" class="yes-no-buttons">
            <el-button
              :type="dspdGoalAnswers[goal.id] === 'yes' ? 'primary' : 'default'"
              size="medium"
              @click="$set(dspdGoalAnswers, goal.id, 'yes')"
            >Yes</el-button>
            <el-button
              :type="dspdGoalAnswers[goal.id] === 'no' ? 'danger' : 'default'"
              size="medium"
              @click="$set(dspdGoalAnswers, goal.id, 'no')"
            >No</el-button>
          </div>
        </div>

        <div v-if="showConcernsField" class="mb-3">
          <label class="form-label">
            Do you have any concerns about this individual today?
            <span style="color: #f56c6c;">*</span>
          </label>
          <el-input
            v-model="dspdConcerns"
            type="textarea"
            :rows="3"
            placeholder="Enter your answer..."
          />
        </div>
      </div>

      <!-- VR shift: admin-defined service code questions -->
      <div v-if="isVRShift && vrServiceCodeQuestions.length && !isSideActor && !showJdFields">
        <div
          v-for="q in vrServiceCodeQuestions"
          :key="'vr-q-' + q.id"
          class="mb-3"
        >
          <label class="form-label">
            {{ q.question_text }}
            <span v-if="q.required" style="color: #f56c6c;">*</span>
          </label>
          <div
            v-if="q.description"
            class="shift-notes-description mb-2"
            style="white-space: pre-line;"
          >{{ q.description }}</div>
          <el-input
            v-if="q.question_type === 'free_response' || !q.question_type"
            :value="vrQuestionAnswers[q.id] || ''"
            @input="$set(vrQuestionAnswers, q.id, $event)"
            type="textarea"
            :rows="2"
            placeholder="Enter your answer..."
          />
          <div v-else-if="q.question_type === 'yes_no'" class="yes-no-buttons">
            <el-button
              :type="vrQuestionAnswers[q.id] === 'yes' ? 'primary' : 'default'"
              size="medium"
              @click="$set(vrQuestionAnswers, q.id, 'yes')"
            >Yes</el-button>
            <el-button
              :type="vrQuestionAnswers[q.id] === 'no' ? 'danger' : 'default'"
              size="medium"
              @click="$set(vrQuestionAnswers, q.id, 'no')"
            >No</el-button>
          </div>
          <el-select
            v-else-if="q.question_type === 'select'"
            :value="vrQuestionAnswers[q.id] || ''"
            @input="$set(vrQuestionAnswers, q.id, $event)"
            placeholder="Select an option"
            style="width: 100%;"
          >
            <el-option
              v-for="(opt, i) in (q.options || [])"
              :key="i"
              :label="opt"
              :value="opt"
            />
          </el-select>
          <el-select
            v-else-if="q.question_type === 'multi_select'"
            multiple
            :value="parseMultiAnswer(vrQuestionAnswers[q.id])"
            @input="$set(vrQuestionAnswers, q.id, joinMultiAnswer($event))"
            placeholder="Select all that apply"
            style="width: 100%;"
          >
            <el-option
              v-for="(opt, i) in (q.options || [])"
              :key="i"
              :label="opt"
              :value="opt"
            />
          </el-select>
        </div>
      </div>

      <!-- Training Shift Fields -->
      <div v-if="isTrainingShift && !isSideActor" class="training-fields-section">
        <h5>Training Details</h5>
        <div class="mb-3">
          <label class="form-label">
            Training Subject
            <span style="color: #f56c6c;">*</span>
          </label>
          <el-select v-model="trainingSubject" placeholder="Select training subject" style="width: 100%;">
            <el-option label="ABI" value="ABI" />
            <el-option label="CPR" value="CPR" />
            <el-option label="SOAR" value="SOAR" />
            <el-option label="Huddle" value="Huddle" />
            <el-option label="Other" value="Other" />
          </el-select>
        </div>
        <div class="mb-3" v-if="trainingSubject === 'Other'">
          <label class="form-label">Training Type</label>
          <el-input
            v-model="trainingType"
            placeholder="e.g., New Hire Training, Ongoing Training, Safety Training"
          />
        </div>
        <div class="mb-3">
          <label class="form-label">Training Title</label>
          <el-input
            v-model="trainingTitle"
            placeholder="e.g., CPR Certification, HIPAA Compliance"
          />
        </div>
        <div class="mb-3">
          <label class="form-label">
            Training Summary / Description
            <span style="color: #f56c6c;">*</span>
          </label>
          <div class="shift-notes-description mb-2">
            <div>
              Please provide a summary of the training including:
              <ul>
                <li>Topics covered during the training</li>
                <li>Key takeaways and skills learned</li>
                <li>Any materials or resources provided</li>
              </ul>
            </div>
          </div>
          <el-input
            v-model="trainingSummary"
            type="textarea"
            :rows="5"
            placeholder="Describe what was covered in the training session..."
          />
        </div>

        <!-- Digital Signature -->
        <div class="mb-3">
          <label class="form-label">
            Signature
            <span style="color: #f56c6c;">*</span>
          </label>
          <div class="signature-container" style="border: 1px solid #dcdfe6; border-radius: 4px; background: #fff;">
            <canvas ref="trainingSignatureCanvas" width="460" height="150" style="width: 100%; display: block; cursor: crosshair;"></canvas>
          </div>
          <div class="mt-1">
            <el-button size="mini" @click="clearTrainingSignature">Clear Signature</el-button>
          </div>
        </div>

        <p class="training-ack-note">
          <i class="fa fa-info-circle"></i>
          A Training Acknowledgement Certificate will be automatically generated upon clock-out.
        </p>
      </div>

      <!-- Non-DSPD, non-JD, non-group shift notes.
           Hidden when admin-configured shift-note questions apply to this
           shift — those questions replace the freeform notes box. -->
      <div class="mb-3" v-if="!isSideActor && !isJDService && !isDSPDGroupShift && !isDSPDOneOnOneShift && !isTrainingShift && !hasShiftNoteQuestions">
        <label class="form-label">Shift Notes</label>
        <div
          v-if="isLSService || isSEDService"
          class="shift-notes-description mb-2"
        >
          <div>
            Please include:
            <ul>
              <li>How your client responded to the lesson</li>
              <li>Issues experienced during the lesson</li>
              <li>Plan for next life skills meeting</li>
              <li>Successful outcomes from the lessons</li>
            </ul>
          </div>
        </div>
        <div v-if="isSBService" class="shift-notes-description mb-2">
          <div>
            Please include:
            <ul>
              <li>
                Update on progress of forms being submitted or response from
                follow up
              </li>
              <li>Any documentation that is still needing to be submitted</li>
            </ul>
          </div>
        </div>
        <div v-if="isJCService" class="shift-notes-description mb-2">
          <div>
            Please include:
            <ul>
              <li>
                Challenges faced or issues holding the client back from
                independence (Most Important)
              </li>
              <li>
                What are we doing to overcome those challenges? Each challenge
                should have a way or idea to implement to overcome it
              </li>
              <li>
                A run down of how the shift went (like a play by play but
                doesn't need to be as specific)
              </li>
              <li>Important work updates</li>
              <li>
                Client successes and where they are doing well and don't need
                support (This will help determine where support can be faded)
              </li>
            </ul>
          </div>
        </div>
        <el-input
          v-model="clockOutNotes"
          type="textarea"
          :rows="vrCodesWithActivities.length || isJCService ? 6 : 3"
          placeholder="Shift Notes"
        />
      </div>
      <!-- Mileage Tracking (shown when mileage or transport was recorded at
           clock-in; required when a starting odometer was entered) -->
      <div v-if="shiftHasMileage" class="mileage-section mb-3">
        <h6><i class="fa-solid fa-car mr-1"></i> Mileage Tracking</h6>
        <div class="mileage-fields">
          <div class="row">
            <div class="col-md-12 mb-2" v-if="myMileageRecord.mileage_start_location || myMileageRecord.odometer_start">
              <small class="text-muted">Start: {{ myMileageRecord.mileage_start_location || 'N/A' }} | Odometer Start: {{ myMileageRecord.odometer_start || 'N/A' }}</small>
            </div>
          </div>
          <div class="row">
            <div class="col-md-6 mb-2">
              <label class="form-label">
                Ending Odometer<span v-if="mileageRequired" class="text-danger"> *</span>
              </label>
              <el-input
                v-model="odometerEnd"
                type="number"
                placeholder="Ending odometer reading"
                size="small"
              />
            </div>
            <div class="col-md-6 mb-2" v-if="computedMiles !== null">
              <label class="form-label">Total Miles</label>
              <div class="computed-miles">{{ computedMiles }} miles</div>
            </div>
          </div>
          <div class="row">
            <div class="col-md-6 mb-2">
              <label class="form-label">
                Places Visited In Between<span v-if="mileageRequired" class="text-danger"> *</span>
              </label>
              <el-input
                v-model="mileageMidLocation"
                placeholder='e.g. Work site, School — or "None"'
                size="small"
              />
            </div>
            <div class="col-md-6 mb-2">
              <label class="form-label">
                Ending Location<span v-if="mileageRequired" class="text-danger"> *</span>
              </label>
              <el-input
                v-model="mileageEndLocation"
                placeholder="e.g. Client's home"
                size="small"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <template slot="footer">
      <div class="text-center">
        <el-button @click="handleCancel">Cancel</el-button>
        <el-button
          type="primary"
          class="clock-out-btn"
          @click="confirmClockOut"
          :loading="clockingOut"
        >
          <i class="fa-solid fa-clock"></i> Clock Out
        </el-button>
      </div>
    </template>
  </AIVDialog>
</template>

<script>
import AIVDialog from "@/components/AIVDialog.vue";
import AIVSelect from "@/components/AIVSelect.vue";
import AIVTimeSelect from "@/components/AIVTimeSelect.vue";
import { shiftsService } from "@/serviceHandlers/shiftsService";
import { geolocationService } from "@/serviceHandlers/geolocationService";
import { jobCoachingActivitiesService } from "src/serviceHandlers/jobCoachingActivitiesService";
import vrActivitiesMixin from "src/mixins/vrActivitiesMixin";
import { clientsService } from "@/serviceHandlers/clientsService";
import { shiftNoteQuestionsService } from "@/serviceHandlers/shiftNoteQuestionsService";
import { enqueue, generateEventId } from "@/utils/offlineQueue";

export default {
  name: "ClockOutModal",
  components: { AIVDialog, AIVSelect, AIVTimeSelect },
  mixins: [vrActivitiesMixin],

  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    shift: {
      type: Object,
      default: () => ({}),
    },
    userId: {
      type: [String, Number],
      required: true,
    },
    preAdministeredMedications: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      clockingOut: false,
      gettingLocation: false,
      currentLocation: null,
      currentLocationAddress: null,
      calculatedDistance: null,
      locationUpdateInterval: null,
      clockOutNotes: "",
      selectedActivities: [],
      selectedSEDActivities: [],
      selectedSBActivities: [],
      jdActivity: "",
      jdOutcome: "",
      jdNextSteps: "",
      selectedJCPrimaryActivities: [],
      selectedJCSecondaryActivities: [],
      jobCoachingActivities: [],
      loadingJobCoachingActivities: false,
      // LS / SED / SB activities are admin-managed in Settings and loaded from
      // the vr_activities table (see fetchVRActivities).
      lsActivities: [],
      sedActivities: [],
      sbActivities: [],
      clientMedicationGroups: [],
      medicationAdministrations: {},
      medicationSkipReasons: [
        { id: "Refused", name: "Refused" },
        { id: "Did not attend", name: "Did not attend" },
        { id: "Not required today", name: "Not required today" },
        { id: "Other", name: "Other" },
      ],
      // DSPD 1-on-1 shift note data
      dspdServiceCodeQuestions: [],
      dspdClientGoals: [],
      dspdQuestionAnswers: {},
      dspdGoalAnswers: {},
      dspdConcerns: "",
      // VR service-code shift questions
      vrServiceCodeQuestions: [],
      vrQuestionAnswers: {},
      mileageMidLocation: '',
      mileageEndLocation: '',
      odometerEnd: null,
      trainingSummary: "",
      trainingType: "",
      trainingTitle: "",
      trainingSubject: "",
      trainingSignaturePad: null,
    };
  },

  computed: {
    // Shadow caregiver on a non-group shift: clocking out only closes
    // their own time record — the primary owns all shift documentation
    isSideActor() {
      const s = this.shift || {};
      const shiftType =
        s.shift_type || s.originalData?.shift_type || "1-on-1";
      if (shiftType === "Group") return false;
      if (s.my_role) return s.my_role !== "primary";
      // my_role only comes from getCurrent; when the shift arrived without
      // it, infer side-actor from an existing side clock row (non-group
      // primaries never have one).
      const rows = s.caregiverClockTimes || s.originalData?.caregiverClockTimes || [];
      return rows.some((r) => Number(r.id_users) === Number(this.userId));
    },
    isVisible: {
      get() {
        return this.visible;
      },
      set(value) {
        this.$emit("update:visible", value);
      },
    },

    isVirtualShift() {
      return !!(this.shift?.virtual_link);
    },

    // Group shifts / shadow roles carry mileage on the caregiver's own
    // clock-time row; fall back to the shift-level fields (1-on-1 primary).
    myMileageRecord() {
      const s = this.shift || {};
      const rows =
        s.caregiverClockTimes || s.originalData?.caregiverClockTimes || [];
      const mine = Array.isArray(rows)
        ? rows.find((r) => r.id_users === this.userId)
        : null;
      return mine && (mine.is_transporting || mine.odometer_start != null)
        ? mine
        : s;
    },

    shiftHasMileage() {
      const rec = this.myMileageRecord;
      return !!(rec.is_transporting || rec.odometer_start != null);
    },

    // A recorded starting odometer must be closed out at clock-out (unless
    // the trip was already closed mid-shift via "Stop Transporting").
    mileageRequired() {
      const rec = this.myMileageRecord;
      return !!(rec.odometer_start != null && rec.odometer_end == null);
    },

    computedMiles() {
      const rec = this.myMileageRecord;
      if (this.odometerEnd && rec.odometer_start != null) {
        const miles = parseFloat(this.odometerEnd) - parseFloat(rec.odometer_start);
        return miles > 0 ? miles.toFixed(1) : null;
      }
      return null;
    },

    requiresLocation() {
      // Show the location/distance panel for geofence-enforced codes
      if (!this.shift) return false;
      if (this.geofenceBypassed) return false;
      const codes = this.shift.service_codes || [];
      if (codes.length === 0) return false;
      // Admin-configurable per service code (Settings → Service Codes).
      if (codes.some(sc => sc.geofence_mode)) {
        return codes.some(sc => sc.geofence_mode === 'enforce');
      }
      // Fallback for payloads predating the geofence_mode column.
      return codes.some(sc => sc.code === 'LS' || sc.code === 'JC');
    },

    // 'off' codes store no location at all: no tracking, no geocode sent.
    locationCaptureDisabled() {
      const codes = this.shift?.service_codes || [];
      return codes.length > 0 && codes.every(sc => sc.geofence_mode === 'off');
    },

    geofenceBypassed() {
      if (!this.shift) return false;
      const caregiver = this.shift.caregiver
        || (this.shift.caregivers && this.shift.caregivers[0])
        || null;
      if (caregiver && caregiver.bypass_geofence) return true;
      const shiftClients = this.shift.clients || this.shift.originalData?.clients || [];
      return shiftClients.some(c => c && c.bypass_geofence);
    },

    clientLocationInfo() {
      if (!this.shift) {
        return null;
      }

      let location = null;
      let address = '';
      let geocode = null;

      // Priority 1: Check if shift has a separate clock-out address
      if (this.shift.use_separate_clock_addresses && this.shift.clockOutAddress?.address1) {
        location = {
          address1: this.shift.clockOutAddress.address1,
          address2: this.shift.clockOutAddress.address2,
          city: this.shift.clockOutAddress.city,
          state: this.shift.clockOutAddress.state,
          zip: this.shift.clockOutAddress.zip,
          geocode: this.shift.clockOutAddress.geocode
        };
      }
      // Priority 2: Check if shift has an alternate address (for VR shifts with "Use Alternate Location" checked)
      else if (this.shift.alternateAddress?.address1) {
        location = {
          address1: this.shift.alternateAddress.address1,
          address2: this.shift.alternateAddress.address2,
          city: this.shift.alternateAddress.city,
          state: this.shift.alternateAddress.state,
          zip: this.shift.alternateAddress.zip,
          geocode: this.shift.alternateAddress.geocode
        };
      } else {
        const clients = this.shift.clients || this.shift.originalData?.clients || [];
        if (clients.length > 0) {
          const client = clients[0];

        if (client.addresses && Array.isArray(client.addresses) && client.addresses.length > 0) {
          const firstAddress = client.addresses[0];
          location = {
            address1: firstAddress.address1 || firstAddress.street || firstAddress.address,
            address2: firstAddress.address2,
            city: firstAddress.city,
            state: firstAddress.state,
            zip: firstAddress.zip || firstAddress.postal_code,
            geocode: firstAddress.geocode
          };
        } else if (client.address && typeof client.address === 'object') {
          location = {
            address1: client.address.address1 || client.address.street || client.address.address,
            address2: client.address.address2,
            city: client.address.city,
            state: client.address.state,
            zip: client.address.zip || client.address.postal_code,
            geocode: client.address.geocode
          };
        } else if (typeof client.address === 'string') {
          location = {
            address1: client.address,
            address2: '',
            city: '',
            state: '',
            zip: '',
            geocode: client.geocode
          };
        }
        }
      }

      if (location) {
        const parts = [];
        if (location.address1) parts.push(location.address1);
        if (location.address2) parts.push(location.address2);
        if (location.city) parts.push(location.city);
        if (location.state) parts.push(location.state);
        if (location.zip) parts.push(location.zip);
        address = parts.join(', ');

        if (location.geocode) {
          geocode = geolocationService.parseGeocodeString(location.geocode);
        }
      }

      return address ? { address, geocode } : null;
    },

    isLSService() {
      if (!this.shift) return false;

      if (this.shift.service_codes && this.shift.service_codes.length > 0) {
        return this.shift.service_codes.some((sc) => sc.code === "LS");
      }

      return false;
    },

    isSEDService() {
      if (!this.shift) return false;

      if (this.shift.service_codes && this.shift.service_codes.length > 0) {
        return this.shift.service_codes.some((sc) => sc.code === "SED");
      }

      return false;
    },

    isSBService() {
      if (!this.shift) return false;

      if (this.shift.service_codes && this.shift.service_codes.length > 0) {
        return this.shift.service_codes.some((sc) => sc.code === "SB");
      }

      return false;
    },

    // Check if the service is Job Development (CE, SE, SJBT, TWE are all JD variations)
    isJDService() {
      if (!this.shift) return false;

      if (this.shift.service_codes && this.shift.service_codes.length > 0) {
        return this.shift.service_codes.some(
          (sc) =>
            sc.code === "CE" ||
            sc.code === "SE" ||
            sc.code === "SJBT" ||
            sc.code === "TWE",
        );
      }

      return false;
    },

    // Legacy JD documentation already stored on the shift's jd_* columns
    // (from before the JD fields became configurable shift_note_questions)
    hasLegacyJdData() {
      const s = this.shift || {};
      const o = s.originalData || {};
      return !!(
        s.jd_activity || s.jd_outcome || s.jd_next_steps ||
        o.jd_activity || o.jd_outcome || o.jd_next_steps
      );
    },

    // Show the bespoke jd_* fields only for shifts already documented that
    // way, or as a fallback when the JD code has no configured questions.
    // Otherwise JD codes document through the generic question blocks.
    showJdFields() {
      return (
        this.isJDService &&
        (this.hasLegacyJdData || this.vrServiceCodeQuestions.length === 0)
      );
    },

    // Legacy DSPD 1-on-1 concerns already stored as shifts.shift_notes
    // (from before the concerns prompt became a configurable question)
    hasLegacyConcerns() {
      const s = this.shift || {};
      const o = s.originalData || {};
      return !!((s.shift_notes || o.shift_notes || "").toString().trim());
    },

    // Show the hardcoded concerns textarea only for shifts already
    // documented that way, or as a fallback when the DSPD code has no
    // configured questions. Otherwise the seeded 'dspd_concerns' question
    // carries the prompt.
    showConcernsField() {
      return (
        this.isDSPDOneOnOneShift &&
        (this.hasLegacyConcerns || this.dspdServiceCodeQuestions.length === 0)
      );
    },

    // The DSPD question list actually rendered: the seeded concerns
    // question is hidden while the legacy concerns textarea is active.
    visibleDspdQuestions() {
      if (!this.showConcernsField) return this.dspdServiceCodeQuestions;
      return this.dspdServiceCodeQuestions.filter(
        (q) => q.question_key !== "dspd_concerns",
      );
    },

    isJCService() {
      if (!this.shift) return false;

      if (this.shift.service_codes && this.shift.service_codes.length > 0) {
        return this.shift.service_codes.some((sc) => sc.code === "JC");
      }

      return false;
    },

    isDSPDGroupShift() {
      if (!this.shift) return false;
      let payer = this.shift.payer;
      if (!payer) {
        const clients = this.shift.clients || [];
        if (clients.length > 0 && clients[0].payer_type) {
          payer = clients[0].payer_type;
        }
      }
      return (
        this.shift.shift_type === "Group" &&
        payer === "DSPD" &&
        this.shift.service_codes &&
        this.shift.service_codes.some(
          (sc) => sc.code === "DSG" || sc.code === "DSP",
        )
      );
    },

    isDSPDOneOnOneShift() {
      if (!this.shift) return false;
      let payer = this.shift.payer;
      if (!payer) {
        const clients = this.shift.clients || [];
        if (clients.length > 0 && clients[0].payer_type) {
          payer = clients[0].payer_type;
        }
      }
      return payer === "DSPD" && !this.isDSPDGroupShift;
    },
    isVRShift() {
      if (!this.shift) return false;
      let payer = this.shift.payer;
      if (!payer) {
        const clients = this.shift.clients || [];
        if (clients.length > 0 && clients[0].payer_type) {
          payer = clients[0].payer_type;
        }
      }
      return payer === "VR";
    },

    // True when this shift has admin-configured shift-note questions
    // (from the shift_note_questions table) applicable to it. When present,
    // those questions replace the freeform "Shift Notes" box at clock-out.
    hasShiftNoteQuestions() {
      return (
        this.vrServiceCodeQuestions.length > 0 ||
        this.dspdServiceCodeQuestions.length > 0
      );
    },

    isTrainingShift() {
      if (!this.shift) return false;
      const payer = this.shift.payer || this.shift.originalData?.payer;
      if (payer !== "Non Service") return false;
      const nonServiceCodes =
        this.shift.non_service_codes ||
        this.shift.nonServiceCodes ||
        this.shift.originalData?.non_service_codes ||
        [];
      // Case-insensitive: the code is admin-editable in Settings and live
      // databases carry "Training" as well as the seeded "TRAINING".
      return nonServiceCodes.some(
        (nsc) => (nsc.code || "").toUpperCase() === "TRAINING",
      );
    },

    myClockInTime() {
      if (!this.isDSPDGroupShift) return null;
      if (!this.shift.caregiverClockTimes) return null;
      const myRecord = this.shift.caregiverClockTimes.find(
        (ct) => Number(ct.id_users) === Number(this.userId),
      );
      return myRecord ? myRecord.clock_in : null;
    },
  },

  mounted() {
    // No auto-selection needed for clock-out
  },

  beforeDestroy() {
    this.clearLocationUpdateTimers();
  },

  watch: {
    visible(newVal) {
      if (newVal) {
        this.resetForm();
        this.loadExistingNotes();
        // Capture-only location: always record where the clock-out happens,
        // even for service codes with no geofence display/enforcement
        // (unless the code's geofence_mode is 'off').
        if (!this.isVirtualShift && !this.locationCaptureDisabled) {
          this.$nextTick(() => {
            this.startLocationTracking();
          });
        }
        this.fetchClientMedications();
        this.fetchDSPDShiftNoteData();
        this.fetchVRShiftNoteData();
        // Initialize training signature pad if this is a training shift
        if (this.isTrainingShift) {
          this.$nextTick(() => this.initTrainingSignaturePad());
        }
      } else {
        this.clearLocationUpdateTimers();
      }
    },

    currentLocation: {
      handler() {
        this.calculateDistance();
      },
      deep: true
    },

    selectedActivities(newVal) {
      if (newVal && newVal.length > 3) {
        this.selectedActivities = newVal.slice(0, 3);
      }
    },

    selectedSEDActivities(newVal) {
      if (newVal && newVal.length > 3) {
        this.selectedSEDActivities = newVal.slice(0, 3);
      }
    },

    selectedSBActivities(newVal) {
      if (newVal && newVal.length > 3) {
        this.selectedSBActivities = newVal.slice(0, 3);
      }
    },

    selectedJCPrimaryActivities(newVal) {
      if (newVal && newVal.length > 3) {
        this.selectedJCPrimaryActivities = newVal.slice(0, 3);
      }
    },

    selectedJCSecondaryActivities(newVal) {
      if (newVal && newVal.length > 3) {
        this.selectedJCSecondaryActivities = newVal.slice(0, 3);
      }
    },

    isJCService(newVal) {
      if (newVal) {
        this.fetchJobCoachingActivities();
      }
    },
  },

  methods: {
    // multi_select answers are stored as a comma-joined string so every
    // read/display/report path renders them as-is; convert to/from the array
    // the multiple-select control needs.
    parseMultiAnswer(value) {
      if (Array.isArray(value)) return value;
      if (value === null || value === undefined || value === "") return [];
      return String(value)
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
    },
    joinMultiAnswer(value) {
      return (Array.isArray(value) ? value : []).join(", ");
    },
    openMapLinkFromAddress(address) {
      const encodedAddress = encodeURIComponent(address);
      const mapUrl = `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
      window.open(mapUrl, '_blank');
    },
    resetForm() {
      this.clockOutNotes = "";
      this.selectedActivities = [];
      this.selectedSEDActivities = [];
      this.selectedSBActivities = [];
      this.jdActivity = "";
      this.jdOutcome = "";
      this.jdNextSteps = "";
      this.selectedJCPrimaryActivities = [];
      this.selectedJCSecondaryActivities = [];
      this.currentLocation = null;
      this.currentLocationAddress = null;
      this.calculatedDistance = null;
      this.clientMedicationGroups = [];
      this.medicationAdministrations = {};
      this.dspdServiceCodeQuestions = [];
      this.dspdClientGoals = [];
      this.dspdQuestionAnswers = {};
      this.dspdGoalAnswers = {};
      this.dspdConcerns = "";
      this.vrServiceCodeQuestions = [];
      this.vrQuestionAnswers = {};
      this.mileageMidLocation = '';
      this.mileageEndLocation = '';
      this.odometerEnd = null;
      this.trainingSummary = "";
      this.trainingType = "";
      this.trainingTitle = "";
      this.trainingSubject = "";
      if (this.trainingSignaturePad) {
        this.trainingSignaturePad.clear();
      }
      this.clearLocationUpdateTimers();
    },

    async fetchClientMedications() {
      if (!this.shift || !this.isDSPDOneOnOneShift) return;
      const shiftClients = this.shift.clients || this.shift?.originalData?.clients || [];
      if (shiftClients.length === 0) return;
      const now = new Date();
      const currentTimeStr = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
      const groups = [];
      const administrations = {};
      for (const client of shiftClients) {
        if (!client.id) continue;
        const clientName = client.name ||
          `${client.first_name || ''} ${client.last_name || ''}`.trim() ||
          `Client ${client.id}`;
        try {
          const response = await clientsService.getMedications(client.id);
          // Filter by active status and by schedule_days (current day of week)
          const currentDayOfWeek = new Date().getDay(); // 0=Sun..6=Sat
          const meds = (response.medications || []).filter(m => {
            if (m.active === false) return false;
            // If schedule_days is set and non-empty, only show on matching days
            if (m.schedule_days && Array.isArray(m.schedule_days) && m.schedule_days.length > 0) {
              return m.schedule_days.includes(currentDayOfWeek);
            }
            return true; // No schedule_days means every day
          });
          if (meds.length > 0) {
            groups.push({ clientId: client.id, clientName, medications: meds });
            for (const med of meds) {
              const key = `${client.id}_${med.id}`;
              const pre = this.preAdministeredMedications[med.id];
              if (pre) {
                let timeStr = currentTimeStr;
                if (pre.administeredAt) {
                  const d = new Date(pre.administeredAt);
                  if (!isNaN(d.getTime())) {
                    timeStr = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
                  }
                }
                const preSkipReason = pre.skipReason || "";
                const preSkipCategory = ["Refused", "Did not attend", "Not required today"].includes(preSkipReason) ? preSkipReason : (preSkipReason ? "Other" : "");
                administrations[key] = {
                  wasAdministered: pre.wasAdministered,
                  skipReason: preSkipReason,
                  skipReasonCategory: preSkipCategory,
                  clientId: client.id,
                  medicationId: med.id,
                  administeredAtTime: pre.wasAdministered ? timeStr : currentTimeStr,
                };
              } else {
                administrations[key] = {
                  wasAdministered: null,
                  skipReason: "",
                  skipReasonCategory: "",
                  clientId: client.id,
                  medicationId: med.id,
                  administeredAtTime: currentTimeStr,
                };
              }
            }
          }
        } catch (e) { /* skip client on error */ }
      }
      this.clientMedicationGroups = groups;
      this.medicationAdministrations = administrations;
    },

    async fetchDSPDShiftNoteData() {
      if (!this.isDSPDOneOnOneShift) return;

      // Fetch service code questions for this shift's service codes
      const serviceCodes = this.shift.service_codes || this.shift.originalData?.service_codes || [];
      if (serviceCodes.length > 0) {
        try {
          const questionPromises = serviceCodes.map((sc) =>
            shiftNoteQuestionsService.getByServiceCode(sc.code).catch(() => []),
          );
          const questionResults = await Promise.all(questionPromises);
          // Flatten and deduplicate
          const allQuestions = [];
          const seenIds = new Set();
          for (const questions of questionResults) {
            for (const q of (questions || [])) {
              if (!seenIds.has(q.id)) {
                seenIds.add(q.id);
                allQuestions.push(q);
              }
            }
          }
          this.dspdServiceCodeQuestions = allQuestions.sort(
            (a, b) => (a.sort_order || 0) - (b.sort_order || 0),
          );
        } catch {
          this.dspdServiceCodeQuestions = [];
        }
      }

      // Fetch client goals
      const shiftClients = this.shift.clients || this.shift.originalData?.clients || [];
      if (shiftClients.length > 0) {
        const clientId = shiftClients[0].id;
        if (clientId) {
          try {
            const response = await clientsService.getGoals(clientId);
            this.dspdClientGoals = response.goals || [];
          } catch {
            this.dspdClientGoals = [];
          }
        }
      }
    },

    async fetchVRShiftNoteData() {
      if (!this.isVRShift) return;

      // Fetch admin-defined service code questions for this shift's VR
      // service codes (managed in Settings -> Edit VR Shift Questions).
      const serviceCodes =
        this.shift.service_codes ||
        this.shift.originalData?.service_codes ||
        [];
      if (serviceCodes.length === 0) {
        this.vrServiceCodeQuestions = [];
        return;
      }
      try {
        const questionResults = await Promise.all(
          serviceCodes.map((sc) =>
            shiftNoteQuestionsService.getByServiceCode(sc.code).catch(() => []),
          ),
        );
        // Flatten and deduplicate by question id
        const allQuestions = [];
        const seenIds = new Set();
        for (const questions of questionResults) {
          for (const q of questions || []) {
            if (!seenIds.has(q.id)) {
              seenIds.add(q.id);
              allQuestions.push(q);
            }
          }
        }
        this.vrServiceCodeQuestions = allQuestions.sort(
          (a, b) => (a.sort_order || 0) - (b.sort_order || 0),
        );
      } catch {
        this.vrServiceCodeQuestions = [];
      }
    },

    async loadExistingNotes() {
      if (!this.shift) return;

      // Load the generic VR activity option lists for this shift's codes, then
      // map any saved selections back to ids.
      await this.loadVrActivities();
      this.loadSelectedVrActivities(this.shift);

      // Load existing shift notes if they exist
      if (this.shift.shift_notes) {
        this.clockOutNotes = this.shift.shift_notes;
      }

      // Load existing JD notes if this is a JD service
      if (this.isJDService) {
        if (this.shift.jd_activity) {
          this.jdActivity = this.shift.jd_activity;
        }
        if (this.shift.jd_outcome) {
          this.jdOutcome = this.shift.jd_outcome;
        }
        if (this.shift.jd_next_steps) {
          this.jdNextSteps = this.shift.jd_next_steps;
        }
      }

      // LS/SED/SB and other generic VR activities are mapped above by
      // loadSelectedVrActivities().

      // Load existing JC activities if they exist
      if (this.isJCService) {
        if (this.shift.jc_primary_activities) {
          let activities = this.shift.jc_primary_activities;
          if (typeof activities === "string") {
            try {
              activities = JSON.parse(activities);
            } catch (error) {
              activities = [];
            }
          }
          if (Array.isArray(activities)) {
            this.selectedJCPrimaryActivities = activities
              .map((activityName) => {
                const activity = this.jobCoachingActivities.find(
                  (act) => act.name === activityName
                );
                return activity ? activity.id : null;
              })
              .filter(Boolean);
          }
        }

        if (this.shift.jc_secondary_activities) {
          let activities = this.shift.jc_secondary_activities;
          if (typeof activities === "string") {
            try {
              activities = JSON.parse(activities);
            } catch (error) {
              activities = [];
            }
          }
          if (Array.isArray(activities)) {
            this.selectedJCSecondaryActivities = activities
              .map((activityName) => {
                const activity = this.jobCoachingActivities.find(
                  (act) => act.name === activityName
                );
                return activity ? activity.id : null;
              })
              .filter(Boolean);
          }
        }
      }
    },

    handleCancel() {
      this.isVisible = false;
      this.resetForm();
    },

    // vrActivities mixin hook: a shift may carry multiple VR codes.
    getShiftVrCodes() {
      return (this.shift?.service_codes || []).map((sc) => sc.code);
    },

    async fetchJobCoachingActivities() {
      if (this.jobCoachingActivities.length > 0) {
        // Already loaded
        return;
      }

      try {
        this.loadingJobCoachingActivities = true;
        const response = await jobCoachingActivitiesService.list();
        this.jobCoachingActivities = response || [];
      } catch (error) {
        this.$notifyError("Failed to load job coaching activities");
      } finally {
        this.loadingJobCoachingActivities = false;
      }
    },

    startLocationTracking() {
      this.requestLocation();

      this.locationUpdateInterval = setInterval(() => {
        this.requestLocation();
      }, 30000);
    },

    clearLocationUpdateTimers() {
      if (this.locationUpdateInterval) {
        clearInterval(this.locationUpdateInterval);
        this.locationUpdateInterval = null;
      }
    },

    async initTrainingSignaturePad() {
      try {
        const SignaturePad = (await import("signature_pad")).default;
        const canvas = this.$refs.trainingSignatureCanvas;
        if (canvas) {
          canvas.width = canvas.parentElement.clientWidth;
          canvas.height = 150;
          this.trainingSignaturePad = new SignaturePad(canvas, {
            backgroundColor: "rgb(255, 255, 255)",
            penColor: "rgb(0, 0, 0)",
          });
        }
      } catch (err) {
      }
    },

    clearTrainingSignature() {
      if (this.trainingSignaturePad) this.trainingSignaturePad.clear();
    },

    calculateDistance() {
      if (!this.currentLocation || !this.clientLocationInfo?.geocode) {
        this.calculatedDistance = null;
        return;
      }

      const distance = geolocationService.calculateDistance(
        this.currentLocation.latitude,
        this.currentLocation.longitude,
        this.clientLocationInfo.geocode.latitude,
        this.clientLocationInfo.geocode.longitude
      );

      this.calculatedDistance = distance;
    },

    formatDistance(miles) {
      if (miles === null || miles === undefined) return 'N/A';

      // Convert to feet if less than 0.5 miles
      if (miles < 0.5) {
        const feet = Math.round(miles * 5280);
        return `${feet} feet`;
      }

      return `${miles.toFixed(2)} miles`;
    },

    async requestLocation() {
      this.gettingLocation = true;

      try {
        const permissionStatus = await geolocationService.checkPermission();
        if (permissionStatus.state === 'denied') {
          this.clearLocationUpdateTimers();
          // Only interrupt the caregiver when the geofence display is actually
          // used; for capture-only codes a missing fix just isn't stored.
          if (this.requiresLocation) {
            this.$notifyError(
              'Location permission is denied. Please enable location access in your browser settings:\n\n' +
              '1. Click the location icon (🔒) in your browser address bar\n' +
              '2. Select "Allow" for location access\n' +
              '3. Refresh the page and try again'
            );
          }
          return;
        }
        if (permissionStatus.state === 'prompt' && this.requiresLocation) {
          this.$notifyInfo('Please allow location access when prompted by your browser to continue.');
        }

        const position = await geolocationService.getCurrentPosition();
        this.currentLocation = {
          latitude: position.latitude,
          longitude: position.longitude,
          accuracy: position.accuracy,
        };
      } catch (error) {
        let errorMessage = '';
        if (error.message.includes('denied')) {
          errorMessage = 'Location access was denied. Please enable location permissions in your browser settings and try again.';
        } else if (error.message.includes('unavailable')) {
          errorMessage = 'Location services are unavailable. Please check your device settings and try again.';
        } else if (error.message.includes('timeout')) {
          errorMessage = 'Location request timed out. Please try again or check your internet connection.';
        } else {
          errorMessage = `Location error: ${error.message}. Please try again or contact your administrator.`;
        }
        if (this.requiresLocation) {
          this.$notifyError(errorMessage);
        }
      } finally {
        this.gettingLocation = false;
      }
    },

    async confirmClockOut() {
      // Geofencing temporarily disabled - location is captured but not required
      // if (this.requiresLocation && !this.currentLocation) {
      //   this.$notifyError("Location is required to clock out for this service code. Please wait for your location to be captured.");
      //   return;
      // }

      // Shadow caregivers don't document the shift — the primary does
      const skipDocs = this.isSideActor;

      // Validate DSPD 1-on-1 shift note data
      if (this.isDSPDOneOnOneShift && !skipDocs) {
        // Validate required service code questions (rendered list only —
        // the seeded concerns question is hidden in legacy-concerns mode)
        for (const q of this.visibleDspdQuestions) {
          if (q.required) {
            const answer = this.dspdQuestionAnswers[q.id];
            if (!answer || !answer.toString().trim()) {
              this.$notifyError(`Please answer: "${q.question_text}"`);
              return;
            }
          }
        }

        // Validate the concerns textarea (only when it is shown)
        if (this.showConcernsField && !this.dspdConcerns.trim()) {
          this.$notifyError("Please answer the concerns question");
          return;
        }
      }

      // Validate required VR service code questions (hidden when the legacy
      // JD fields are active — those carry the documentation instead)
      if (this.isVRShift && !this.showJdFields && !skipDocs) {
        for (const q of this.vrServiceCodeQuestions) {
          if (q.required) {
            const answer = this.vrQuestionAnswers[q.id];
            if (!answer || !answer.toString().trim()) {
              this.$notifyError(`Please answer: "${q.question_text}"`);
              return;
            }
          }
        }
      }

      // Validate notes for non-JD, non-group, non-DSPD-1on1, non-training
      // services. Must mirror the notes box's render condition exactly —
      // training shifts hide the box (they document via summary/subject/
      // signature, validated below), and shift-note questions replace it.
      if (!skipDocs && !this.isJDService && !this.isDSPDGroupShift && !this.isDSPDOneOnOneShift && !this.isTrainingShift && !this.hasShiftNoteQuestions) {
        if (!this.clockOutNotes.trim()) {
          this.$notifyError("Shift Notes are required");
          return;
        }

        if (this.clockOutNotes.length < 20) {
          this.$notifyError("Shift Notes must be at least 20 characters");
          return;
        }
      }

      // Validate legacy Job Development fields (only when they are shown)
      if (this.showJdFields && !skipDocs) {
        if (!this.jdActivity.trim()) {
          this.$notifyError("Job Development Activity is required");
          return;
        }
        if (this.jdActivity.length < 20) {
          this.$notifyError(
            "Job Development Activity must be at least 20 characters",
          );
          return;
        }
        if (!this.jdOutcome.trim()) {
          this.$notifyError("Outcome of Activity is required");
          return;
        }
        if (this.jdOutcome.length < 20) {
          this.$notifyError(
            "Outcome of Activity must be at least 20 characters",
          );
          return;
        }
        if (!this.jdNextSteps.trim()) {
          this.$notifyError("Next Steps Forward is required");
          return;
        }
        if (this.jdNextSteps.length < 20) {
          this.$notifyError(
            "Next Steps Forward must be at least 20 characters",
          );
          return;
        }
      }

      // Validate JC specific requirements
      if (this.isJCService && !skipDocs) {
        if (this.selectedJCPrimaryActivities.length === 0) {
          this.$notifyError(
            "Please select at least one primary job coaching activity",
          );
          return;
        }
        if (this.selectedJCSecondaryActivities.length === 0) {
          this.$notifyError(
            "Please select at least one secondary job coaching activity",
          );
          return;
        }
        if (this.selectedJCPrimaryActivities.length > 3) {
          this.$notifyError("Please select no more than 3 primary activities");
          return;
        }
        if (this.selectedJCSecondaryActivities.length > 3) {
          this.$notifyError(
            "Please select no more than 3 secondary activities",
          );
          return;
        }
      }

      // Validate generic VR activities (each VR code with a list requires >=1).
      const vrActivityError = this.validateVrActivities({ requireMin: true });
      if (vrActivityError && !skipDocs) {
        this.$notifyError(vrActivityError);
        return;
      }

      // Validate medication administration
      for (const group of this.clientMedicationGroups) {
        for (const med of group.medications) {
          const key = `${group.clientId}_${med.id}`;
          const ma = this.medicationAdministrations[key];
          if (!ma || ma.wasAdministered === null) {
            this.$notifyError(`Please indicate whether "${med.name}" was given or skipped`);
            return;
          }
          if (ma.wasAdministered === false) {
            if (!ma.skipReasonCategory) {
              this.$notifyError(`Please select a reason for skipping "${med.name}"`);
              return;
            }
            if (ma.skipReasonCategory === 'Other' && !ma.skipReason.trim()) {
              this.$notifyError(`Please specify the reason for skipping "${med.name}"`);
              return;
            }
          }
          if (ma.wasAdministered === true) {
            const timeStr = ma.administeredAtTime;
            if (!timeStr) {
              this.$notifyError(`Please select a time for "${med.name}"`);
              return;
            }
            // Validate time is within shift window (between clock-in and now)
            const clockInRaw = this.shift?.clock_in || this.shift?.clockIn || this.shift?.originalData?.clockIn || this.shift?.originalData?.clock_in || this.shift?.actualStartTime;
            if (clockInRaw) {
              const clockInDate = new Date(clockInRaw);
              const now = new Date();
              const [h, m] = timeStr.split(':').map(Number);
              const medTime = new Date();
              medTime.setHours(h, m, 0, 0);
              if (medTime < clockInDate) {
                this.$notifyError(`Time for "${med.name}" cannot be before clock-in time`);
                return;
              }
              if (medTime > now) {
                this.$notifyError(`Time for "${med.name}" cannot be in the future`);
                return;
              }
            }
          }
        }
      }

      // Mileage: a starting odometer recorded at clock-in must be closed out
      if (this.mileageRequired) {
        if (!this.odometerEnd) {
          this.$notifyError(
            "Ending odometer reading is required — you entered a starting odometer at clock-in",
          );
          return;
        }
        if (
          parseFloat(this.odometerEnd) <
          parseFloat(this.myMileageRecord.odometer_start)
        ) {
          this.$notifyError(
            "Ending odometer reading cannot be less than the starting reading",
          );
          return;
        }
        if (!this.mileageMidLocation.trim()) {
          this.$notifyError(
            'Places visited is required — enter "None" if you drove straight there',
          );
          return;
        }
        if (!this.mileageEndLocation.trim()) {
          this.$notifyError("Ending location is required");
          return;
        }
      }

      // Validate training shift requirements
      if (this.isTrainingShift && !skipDocs) {
        if (!this.trainingSubject) {
          this.$notifyError("Training subject is required for training shifts");
          return;
        }
        if (!this.trainingSummary.trim()) {
          this.$notifyError("Training summary is required for training shifts");
          return;
        }
        if (this.trainingSignaturePad && this.trainingSignaturePad.isEmpty()) {
          this.$notifyError("Signature is required for training shifts");
          return;
        }
      }

      this.clockingOut = true;
      try {
        const now = new Date();
        const payload = {
          shiftId: this.shift.id,
          caregiverId: this.userId,
          utcTime: now.toISOString(),
          notes: this.clockOutNotes.trim(),
          // Idempotency key: a retried upload (offline queue, lost response)
          // replays the stored outcome instead of double-applying.
          clientEventId: generateEventId(),
        };

        // Add GPS coordinates if available
        if (this.currentLocation && !this.locationCaptureDisabled) {
          payload.geocode = `${this.currentLocation.latitude},${this.currentLocation.longitude}`;
        }

        // Add service-specific data
        Object.assign(payload, {
          // Generic VR activities (LS/SED/SB and any other VR code), keyed by
          // service code.
          vrActivitiesSelected: this.buildVrActivitiesPayload(),
          // Job Development (CE/SE/SJBT/TWE) specific data
          isJDService: this.isJDService,
          jdActivity: this.showJdFields ? this.jdActivity : null,
          jdOutcome: this.showJdFields ? this.jdOutcome : null,
          jdNextSteps: this.showJdFields ? this.jdNextSteps : null,
          // JC specific data
          isJCService: this.isJCService,
          jcPrimaryActivities: this.isJCService
            ? this.selectedJCPrimaryActivities
                .map(
                  (actId) =>
                    this.jobCoachingActivities.find((act) => act.id === actId)
                      ?.name,
                )
                .filter(Boolean)
            : null,
          jcSecondaryActivities: this.isJCService
            ? this.selectedJCSecondaryActivities
                .map(
                  (actId) =>
                    this.jobCoachingActivities.find((act) => act.id === actId)
                      ?.name,
                )
                .filter(Boolean)
            : null,
          // Medication administration data
          medicationAdministrations: Object.values(this.medicationAdministrations).map(ma => ({
            clientId: ma.clientId,
            medicationId: ma.medicationId,
            wasAdministered: ma.wasAdministered,
            skipReason: ma.skipReason || null,
            administeredAt: ma.wasAdministered ? this.buildAdministeredAtISO(ma.administeredAtTime) : null,
          })),
        });

        // Add DSPD 1-on-1 shift note data
        if (this.isDSPDOneOnOneShift) {
          if (this.showConcernsField) {
            payload.notes = this.dspdConcerns.trim();
          }
          payload.questionAnswers = Object.entries(this.dspdQuestionAnswers)
            .filter(([, answer]) => answer != null && answer.toString().trim())
            .map(([questionId, answer]) => ({
              questionId: Number(questionId),
              answer: answer.toString().trim(),
            }));
          payload.goalAnswers = Object.entries(this.dspdGoalAnswers)
            .filter(([, answer]) => answer != null && answer.toString().trim())
            .map(([goalId, answer]) => ({
              goalId: Number(goalId),
              answer: answer.toString().trim(),
            }));
        }

        // Add VR service-code question answers
        if (this.isVRShift) {
          payload.questionAnswers = Object.entries(this.vrQuestionAnswers)
            .filter(([, answer]) => answer != null && answer.toString().trim())
            .map(([questionId, answer]) => ({
              questionId: Number(questionId),
              answer: answer.toString().trim(),
            }));
        }

        // Add mileage data when the shift has mileage tracking
        if (this.shiftHasMileage) {
          payload.mileageMidLocation = this.mileageMidLocation || null;
          payload.mileageEndLocation = this.mileageEndLocation || null;
          payload.odometerEnd = this.odometerEnd ? parseFloat(this.odometerEnd) : null;
        }

        // Add training shift data
        if (this.isTrainingShift) {
          payload.trainingSummary = this.trainingSummary.trim();
          payload.trainingType = this.trainingType.trim() || null;
          payload.trainingTitle = this.trainingTitle.trim() || null;
          payload.trainingSubject = this.trainingSubject || null;
          if (this.trainingSignaturePad && !this.trainingSignaturePad.isEmpty()) {
            payload.trainingSignature = this.trainingSignaturePad.toDataURL();
          }
        }

        let response;
        try {
          response = await shiftsService.clockOut(payload);
        } catch (error) {
          // No connection: save the clock-out (documentation included) on
          // this device and upload when the network returns.
          const isNetworkFailure =
            !navigator.onLine || (error.isAxiosError && !error.response);
          if (!isNetworkFailure) throw error;
          payload.offlineSync = true;
          const clientNames = (this.shift.clients || [])
            .map((c) => c.name || `${c.first_name || ""} ${c.last_name || ""}`.trim())
            .filter(Boolean)
            .join(", ");
          enqueue("clock-out", payload, {
            shiftKey: payload.shiftId,
            label: `Clock-out${clientNames ? ` — ${clientNames}` : ""}`,
          });
          this.$notifySuccess(
            "No internet connection — your clock-out and notes were saved on this device and will upload automatically when you're back online.",
          );
          this.isVisible = false;
          this.resetForm();
          this.$emit("clock-out-success", {
            offlineQueued: true,
            shift: { ...this.shift, id: payload.shiftId },
          });
          return;
        }
        if (this.isTrainingShift) {
          this.$notifySuccess("Successfully clocked out! Training certificate has been generated.");
        } else {
          this.$notifySuccess("Successfully clocked out!");
        }
        this.isVisible = false;
        this.resetForm();
        this.$emit("clock-out-success", response);
      } catch (error) {
        // Geofencing temporarily disabled
        // if (error.response?.status === 403 && error.response?.data?.distance) {
        //   const data = error.response.data;
        //   const distanceMiles = data.distance?.toFixed(2) || "unknown";
        //   const clientAddress = data.clientAddress || "client location";
        //   this.$notifyError(
        //     `Cannot clock out: You are currently ${distanceMiles} miles away from the client location (${clientAddress}). ` +
        //       `You must be within 5 miles of the client's address to clock out.`,
        //   );
        // } else {
        const data = error.response?.data;
        let errorText = data?.error || data?.message || error.message;
        if (Array.isArray(data?.incompleteTasks) && data.incompleteTasks.length) {
          const nameById = {};
          (this.shift.clients || []).forEach((c) => {
            nameById[c.id] =
              c.name || `${c.first_name || ""} ${c.last_name || ""}`.trim();
          });
          const remaining = data.incompleteTasks
            .map((t) =>
              nameById[t.clientId] ? `${t.task} — ${nameById[t.clientId]}` : t.task,
            )
            .join(", ");
          errorText += `. Remaining: ${remaining}`;
        }
        this.$notifyError("Error clocking out: " + errorText);
        // }
      } finally {
        this.clockingOut = false;
      }
    },

    buildAdministeredAtISO(timeStr) {
      if (!timeStr) return new Date().toISOString();
      const [hours, minutes] = timeStr.split(':').map(Number);
      const dt = new Date();
      // Use shift date if available
      if (this.shift?.date) {
        const shiftDateStr = this.shift.date.split('T')[0];
        const [y, mo, d] = shiftDateStr.split('-').map(Number);
        dt.setFullYear(y, mo - 1, d);
      }
      dt.setHours(hours, minutes, 0, 0);
      return dt.toISOString();
    },

    formatTableDate(date) {
      if (!date) return "";
      // Handle date string to avoid timezone issues
      let dateStr = date;
      if (dateStr.includes("T")) {
        dateStr = dateStr.split("T")[0]; // Extract just the date part
      }

      // Create date object with explicit local timezone to avoid UTC interpretation
      const [year, month, day] = dateStr.split("-");
      const dateObj = new Date(year, month - 1, day); // month is 0-indexed

      return dateObj.toLocaleDateString("en-US", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    },

    formatScheduledTime(timeStr, date = null) {
      if (!timeStr) return "N/A";

      // If timeStr is already a full datetime (ISO string), use it directly
      if (timeStr.includes("T")) {
        const utcDate = new Date(timeStr);
        if (!isNaN(utcDate.getTime())) {
          return utcDate.toLocaleTimeString("en-US", {
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
          });
        }
      }

      // Legacy: If we have a date, combine it with the time and treat as UTC
      if (date) {
        const dateOnly = date.split("T")[0];
        const utcDateTime = `${dateOnly}T${timeStr}Z`;
        const utcDate = new Date(utcDateTime);

        if (!isNaN(utcDate.getTime())) {
          return utcDate.toLocaleTimeString("en-US", {
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
          });
        }
      }

      // Fallback: treat as local time
      return this.formatTime12Hour(timeStr);
    },

    formatTime12Hour(timeStr) {
      if (!timeStr) return "";
      const [hour, minute] = timeStr.split(":").map(Number);
      const period = hour >= 12 ? "PM" : "AM";
      const displayHour = hour % 12 || 12;
      return `${displayHour}:${minute.toString().padStart(2, "0")}${period}`;
    },

    formatTimeRange(startTime, endTime, date = null) {
      // Handle new UTC datetime format (with 'T' in the string)
      if (startTime && startTime.includes("T")) {
        const start = this.formatScheduledTime(startTime);
        const end = this.formatScheduledTime(endTime);
        return `${start} - ${end}`;
      }

      // Legacy: handle old format with separate date and time
      if (date && startTime && endTime) {
        const start = this.formatScheduledTime(startTime, date);
        const end = this.formatScheduledTime(endTime, date);
        return `${start} - ${end}`;
      }

      if (!startTime || !endTime) return "";

      // Fallback: Convert UTC times to local times first and format
      const localStartTime = this.convertUTCTimeToLocalTime(startTime);
      const localEndTime = this.convertUTCTimeToLocalTime(endTime);

      const formatTime = (timeString) => {
        const timeParts = timeString.split(":");
        let hours = parseInt(timeParts[0]);
        const minutes = timeParts[1] || "00";
        const ampm = hours >= 12 ? "pm" : "am";
        hours = hours % 12;
        hours = hours ? hours : 12;
        const formattedMinutes = minutes === "00" ? "00" : minutes;
        return `${hours}:${formattedMinutes}${ampm}`;
      };

      return `${formatTime(localStartTime)} - ${formatTime(localEndTime)}`;
    },

    formatClockTime(clockTime) {
      if (!clockTime) return "Not available";

      // Create a Date object from the UTC timestamp
      const utcDate = new Date(clockTime);
      if (isNaN(utcDate.getTime())) return "Invalid time";

      // Format to local time in 12-hour format
      return utcDate.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    },

    convertUTCDateTimeToLocalTime(utcDateTimeStr) {
      if (!utcDateTimeStr) return "";

      // Parse the datetime string - it should already be a proper UTC timestamp from backend
      const utcDate = new Date(utcDateTimeStr);
      if (isNaN(utcDate.getTime())) return "";

      // Convert to local time string (HH:MM format)
      return utcDate.toLocaleTimeString("en-US", {
        hour12: false,
        hour: "2-digit",
        minute: "2-digit",
      });
    },

    convertUTCTimeToLocalTime(timeStr) {
      if (!timeStr || !this.shift?.date) return timeStr;

      try {
        const shiftDate = this.shift.date.split("T")[0];
        const utcDateTime = `${shiftDate}T${timeStr}Z`;
        const utcDate = new Date(utcDateTime);

        if (isNaN(utcDate.getTime())) {
          return timeStr;
        }

        return utcDate.toLocaleTimeString("en-US", {
          hour12: false,
          hour: "2-digit",
          minute: "2-digit",
        });
      } catch (error) {
        return timeStr;
      }
    },

    formatClient(shift) {
      if (!shift) return "";
      if (shift.clients && shift.clients.length > 0) {
        // Show all clients
        return shift.clients.map(client => {
          const name =
            client.name ||
            `${client.first_name || ""} ${client.last_name || ""}`.trim();
          return name || "Unnamed Client";
        }).join(', ');
      }
      return shift.client || "Unassigned";
    },

    formatAdminTime(timeStr) {
      if (!timeStr) return '';
      const [h, m] = timeStr.split(':');
      const hour = parseInt(h);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const displayHour = hour % 12 || 12;
      return `${displayHour}:${m} ${ampm}`;
    },

    formatCaregiver(shift) {
      if (!shift) return "";
      if (shift.caregivers && shift.caregivers.length > 0) {
        // Show all caregivers
        return shift.caregivers.map(caregiver => {
          const name =
            caregiver.name ||
            `${caregiver.first_name || ""} ${caregiver.last_name || ""}`.trim();
          return name || "Unnamed Caregiver";
        }).join(', ');
      }
      return shift.caregiver || "Unassigned";
    },

    formatServiceCode(shift) {
      if (!shift) return "Not specified";

      // Check for non-service codes first (for Non Service shifts)
      const nonServiceCodes = shift.non_service_codes || shift.nonServiceCodes || shift.originalData?.non_service_codes;
      if (nonServiceCodes && nonServiceCodes.length > 0) {
        const codes = nonServiceCodes.map((sc) => sc.code).filter(Boolean);
        return codes.length > 0 ? codes.join(", ") : "Not specified";
      }

      // Check direct service_codes array (from by-role API)
      if (shift.service_codes && shift.service_codes.length > 0) {
        const codes = shift.service_codes.map((sc) => sc.code).filter(Boolean);
        return codes.length > 0 ? codes.join(", ") : "Not specified";
      }

      return "Not specified";
    },
  },

  mounted() {
    // Pre-load job coaching activities if this is a JC service
    if (this.isJCService) {
      this.fetchJobCoachingActivities();
    }
  },
};
</script>

<style scoped>

.mileage-section {
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding-top: 12px;
}

.mileage-fields {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 6px;
  padding: 12px;
}

.computed-miles {
  font-size: 18px;
  font-weight: 600;
  color: #67c23a;
  padding-top: 4px;
}

.medication-client-name {
  color: #6BA6CD;
  font-weight: 600;
  font-size: 0.9rem;
  margin: 0.75rem 0 0.4rem;
}

.medication-admin-item {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 6px;
  padding: 0.75rem;
}

.med-time-hint {
  color: #a0a0a0;
  font-size: 0.85rem;
}

.med-action-buttons {
  display: flex;
  gap: 0.5rem;
}

.med-initial {
  font-size: 0.8rem;
  color: #a0cfee;
  display: flex;
  align-items: center;
  gap: 0.4rem;
}

.clock-modal-content {
  max-width: 600px;
  min-width: 500px;
  margin: 0 auto;
  padding: 0 2rem;
}

.clock-modal-content .mb-3 {
  margin-bottom: 1.5rem;
}

.clock-modal-content h5 {
  color: #fff;
  margin-bottom: 1rem;
  font-weight: 600;
  border-bottom: 2px solid #6ba6cd;
  padding-bottom: 0.5rem;
  font-size: 1.1rem;
}

.clock-modal-content p {
  margin-bottom: 0.5rem;
  color: #e0e0e0;
  font-size: 0.95rem;
}

.clock-modal-content strong {
  color: #fff;
  font-weight: 600;
}

.form-label {
  color: #fff;
  font-weight: 600;
  margin-bottom: 0.5rem;
  display: block;
  font-size: 0.95rem;
}

:deep(.el-textarea__inner) {
  background-color: #656565 !important;
  border: 1px solid #bdbdbd !important;
  color: #ffffff !important;
  border-radius: 6px !important;
  font-size: 14px !important;
  font-weight: normal !important;
}

:deep(.el-textarea__inner):focus {
  border-color: #ffffff !important;
  box-shadow: none !important;
}

:deep(.el-textarea__inner::placeholder) {
  color: rgba(255, 255, 255, 0.5) !important;
}

@media (max-width: 768px) {
  /* Make modal wider on mobile devices */
  :deep(.el-dialog) {
    width: 95% !important;
    margin: 5px auto !important;
  }

  .clock-modal-content {
    min-width: auto !important;
    padding: 0 1rem;
  }
}

.yes-no-buttons {
  display: flex;
  gap: 0.5rem;
}

.shift-notes-description {
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.95rem;
}

.shift-notes-description ul {
  margin: 0.5rem 0;
  padding-left: 1.5rem;
}

.shift-notes-description li {
  margin-bottom: 0.25rem;
}

/* Clock Out button - matches "Clocked Out" status color (muted orange) */
.clock-out-btn {
  background: linear-gradient(
    135deg,
    rgba(237, 137, 54, 0.7),
    rgba(237, 137, 54, 0.5)
  ) !important;
  border: 1px solid rgba(255, 255, 255, 0.2) !important;
  color: #fff !important;
}
.clock-out-btn:hover {
  background: linear-gradient(
    135deg,
    rgba(237, 137, 54, 0.8),
    rgba(237, 137, 54, 0.6)
  ) !important;
  border: 1px solid rgba(255, 255, 255, 0.3) !important;
  color: #fff !important;
}

.virtual-link-value {
  color: #6BA6CD;
  font-size: 0.9rem;
  text-align: right;
  word-break: break-all;
  text-decoration: underline;
}

.virtual-link-value:hover {
  color: #8BC4E0;
}

/* Location Section Styles */
.location-info-card {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1rem;
}

.location-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
  gap: 1rem;
}

.location-row:last-child {
  margin-bottom: 0;
}

.location-label {
  color: #fff;
  font-weight: 600;
  font-size: 0.9rem;
  white-space: nowrap;
  min-width: 140px;
}

.location-label i {
  color: #6BA6CD;
  margin-right: 0.5rem;
  width: 16px;
  text-align: center;
}

.location-value {
  color: #e0e0e0;
  font-size: 0.9rem;
  text-align: right;
  word-break: break-all;
}

.location-pending {
  color: #b0b0b0;
  font-size: 0.9rem;
  padding: 0.5rem 0;
}

.training-fields-section {
  margin-bottom: 1.5rem;
}

.training-fields-section h5 {
  color: #fff;
  margin-bottom: 1rem;
  font-weight: 600;
  border-bottom: 2px solid #6ba6cd;
  padding-bottom: 0.5rem;
  font-size: 1.1rem;
}

.training-ack-note {
  color: #6ba6cd;
  font-size: 0.85rem;
  font-style: italic;
  margin-top: 0.5rem;
}
</style>
